/**
 * Pending Sites Service
 * Handles API calls for pending guest blog sites approval workflow
 */

import { apiService } from './api';

export interface PendingSite {
  id: string;
  site_url: string;
  da: number;
  dr: number;
  ahrefs_traffic: number;
  ss: number | null;
  category: string;
  country: string;
  site_language: string;
  tat: string;
  base_price: number;
  li_base_price: number | null;
  publisher_email: string;
  publisher_name: string;
  keywords?: number | null;
  domainAge?: string | null;
  linkType?: string | null;
  status: 'PENDING' | 'ACCEPTED' | 'REJECTED';
  action_type: 'CREATE' | 'UPDATE';
  existing_site_id: string | null;
  old_price: number | null;
  price_difference: number | null;
  source: string;
  submitted_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
  rejection_reason: string | null;
  existingSite?: {
    id: string;
    site_url: string;
    base_price: number;
    publisher?: {
      publisherName: string;
      email: string;
    };
  };
  matchedExistingSite?: {
    id: string;
    site_url: string;
    base_price: number;
    publisher?: {
      publisherName: string;
      email: string;
    };
  } | null;
  /** Publisher on /sites before this Price Update (saved on accept). */
  previousPublisher?: {
    publisherName: string | null;
    email: string | null;
  } | null;
  reviewer?: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

export interface PendingSitesResponse {
  success: boolean;
  data: {
    pendingSites: PendingSite[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

export interface PendingSitesStats {
  pending: number;
  accepted: number;
  rejected: number;
  total: number;
}

export interface AcceptRejectResponse {
  success: boolean;
  message?: string;
  code?: 'HIGHER_PRICE' | 'NEEDS_OVERRIDE_CONFIRMATION' | string;
  error?: string;
  data?: {
    action_type?: string;
    site_url?: string;
    existing_site_url?: string;
    existing_price?: number;
    new_price?: number;
    savings?: number;
  };
}

export interface BulkAcceptNeedsConfirmation {
  id: string;
  site_url: string;
  existing_site_url?: string;
  existing_price: number;
  new_price: number;
  savings: number;
}

export interface BulkAcceptResponse {
  success: boolean;
  message: string;
  data: {
    accepted: string[];
    failed: {
      id: string;
      error: string;
      code?: string;
      site_url?: string;
      existing_price?: number;
      new_price?: number;
    }[];
    needsConfirmation?: BulkAcceptNeedsConfirmation[];
  };
}

class PendingSitesService {
  private baseUrl = '/pending-sites';

  /**
   * Get list of pending sites with pagination and filtering
   */
  async getPendingSites(params: {
    status?: string;
    page?: number;
    limit?: number;
    search?: string;
  } = {}): Promise<PendingSitesResponse> {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);

    const url = `${this.baseUrl}?${queryParams.toString()}`;
    return apiService.get<PendingSitesResponse>(url);
  }

  /**
   * Get stats for pending sites
   */
  async getStats(): Promise<{ success: boolean; data: PendingSitesStats }> {
    return apiService.get<{ success: boolean; data: PendingSitesStats }>(`${this.baseUrl}/stats`);
  }

  /**
   * Accept a pending site
   * Pass confirmOverride: true after user confirms a lower-price override of /sites
   */
  async acceptSite(
    id: string,
    options: { confirmOverride?: boolean } = {}
  ): Promise<AcceptRejectResponse> {
    return apiService.post<AcceptRejectResponse>(`${this.baseUrl}/${id}/accept`, {
      confirmOverride: Boolean(options.confirmOverride),
    });
  }

  /**
   * Reject a pending site with reason
   */
  async rejectSite(id: string, reason?: string): Promise<AcceptRejectResponse> {
    return apiService.post<AcceptRejectResponse>(`${this.baseUrl}/${id}/reject`, { reason });
  }

  /**
   * Accept multiple pending sites at once
   */
  async acceptBulk(
    ids: string[],
    options: { confirmOverrideIds?: string[] } = {}
  ): Promise<BulkAcceptResponse> {
    return apiService.post<BulkAcceptResponse>(`${this.baseUrl}/accept-bulk`, {
      ids,
      confirmOverrideIds: options.confirmOverrideIds || [],
    });
  }

  /**
   * Delete a pending site record
   */
  async deletePendingSite(id: string): Promise<{ success: boolean; message: string }> {
    return apiService.delete<{ success: boolean; message: string }>(`${this.baseUrl}/${id}`);
  }

  /**
   * Format price for display
   */
  formatPrice(price: number | null | undefined): string {
    if (price === null || price === undefined) return '-';
    return `$${price.toFixed(2)}`;
  }

  /**
   * Format price difference with color indicator
   */
  formatPriceDifference(difference: number | null | undefined): { text: string; color: string } {
    if (difference === null || difference === undefined) return { text: '-', color: 'inherit' };
    
    if (difference > 0) {
      return { text: `-$${difference.toFixed(2)}`, color: 'success.main' };
    } else if (difference < 0) {
      return { text: `+$${Math.abs(difference).toFixed(2)}`, color: 'error.main' };
    }
    return { text: '$0.00', color: 'inherit' };
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /**
   * Get action type label
   */
  getActionTypeLabel(actionType: 'CREATE' | 'UPDATE'): { text: string; color: 'primary' | 'warning' } {
    return actionType === 'CREATE'
      ? { text: 'New Site', color: 'primary' }
      : { text: 'Price Update', color: 'warning' };
  }

  /**
   * Get status label
   */
  getStatusLabel(status: 'PENDING' | 'ACCEPTED' | 'REJECTED'): { text: string; color: 'warning' | 'success' | 'error' } {
    switch (status) {
      case 'PENDING':
        return { text: 'Pending', color: 'warning' };
      case 'ACCEPTED':
        return { text: 'Accepted', color: 'success' };
      case 'REJECTED':
        return { text: 'Rejected', color: 'error' };
    }
  }
}

export const pendingSitesService = new PendingSitesService();
